import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { ErrorCode } from '/imports/errors';
import { checkPasswordRules, validateEmail } from '/imports/utils';
import { Button } from '/imports/ui/Button';
import { Input } from '/imports/ui/Input';
import { PublicFooter } from '/imports/ui/PublicFooter';
import { Loading } from '/imports/ui/Loading';
import { SignedOutPageStyled } from '/imports/ui/SignedOutPage';
import { Checkbox } from '/imports/ui/Checkbox';
import { signupUser } from '/imports/api/users';
import { Logo } from '/imports/ui/Logo';
import { Modal } from '/imports/ui/Modal';
import { termsHtml } from '/imports/ui/terms';
import { DiscordButton, GoogleButton } from '/imports/ui/Oauth2Buttons';

const SignupPageStyled = styled(SignedOutPageStyled)`
  .agree-tos {
    margin-bottom: 1rem;;
  }
  .referral-checkbox {
    margin-bottom: 1.5rem;
  }
  input[type=checkbox] {
    position: relative;
    top: 0.65rem;
  }
`;

/** Account signup form with email/password, OAuth buttons, and terms acceptance. */
export const SignupPage = () => {
  return null;
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [agreeTos, setAgreeTos] = useState(false);
  const [error, setError] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  /**
   * Validates inputs and submits the signup, navigating to confirmation on success.
   * @param e - form submit event
   */
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');
    setUsernameError('');
    setPasswordError('');
    if (!validateEmail(username)) {
      setUsernameError(strings.InvalidEmailAddress());
      return;
    }
    if (!checkPasswordRules(password)) {
      setPasswordError(strings.InvalidPasswordError());
      return;
    }
    setSubmitting(true);
    try {
      await signupUser.call({ username, password });
      navigate(`/signup-submitted?email=${btoa(username)}`);
    }
    catch (error: any) {
      if (error.error === ErrorCode.accountExists) setUsernameError(strings.UsernameAlreadyTaken());
      else toast.error(`${error}`);
    }
    finally {
      setSubmitting(false);
    }
  };
  return (
    <SignupPageStyled className="page">
      <div className="wrapper">
        <div className="inner-wrapper">
          <div><Logo size={3} /></div>
          <div>
            <h2>{strings.SignUp()}</h2>
            <div>{strings.CreateFreeAccount()}</div>
          </div>
          <form onSubmit={onSubmit}>
            <div className="inputs">
              <label>{strings.Email()}</label>
              <Input inputId="login-username" placeholder="mail@website.com" value={username} autoFocus={true} hasError={!!usernameError}
                onChange={(username) => setUsername(username || '')} />
              {usernameError && <div className="form-error">{usernameError}</div>}
              <label>{strings.Password()}</label>
              <Input inputId="login-password" type="password" placeholder="••••••••••••" value={password} hasError={!!passwordError}
                onChange={(password) => setPassword(password || '')} autoComplete='off' onFocus={(e) => e.target.removeAttribute('readonly')} />
              {passwordError && <div className="form-error">{passwordError}</div>}
            </div>
            <div className="agree-tos">
              <Checkbox id="signup-tos" checked={agreeTos} onChange={(checked) => setAgreeTos(checked)}>
                {strings.iAgreeToTheTermsOfService()}&nbsp;
                <a href="#" onClick={() => setShowTerms(true)}>{strings.TermsOfService()}</a>
              </Checkbox>
            </div>
            <div className="login-error">{error}</div>
            <div className="buttons">
              <Button id="login-submit" type="submit" className={`btn ${submitting ? 'tertiary' : 'primary'}`} disabled={!username || !password || !agreeTos}>
                {
                  submitting ?
                    <Loading />
                    :
                    <div className="button-label">{strings.Submit()}</div>
                }
              </Button>
              <GoogleButton />
              <DiscordButton />
            </div>
          </form>
          <span>
            {strings.AlreadyHaveAnAccount()}&nbsp;
            <Link className="login-link" to='/login'>{strings.SignIn()}</Link>
          </span>
        </div>
      </div>
      <PublicFooter />
      {
        showTerms ?
          <Modal
            onClose={() => setShowTerms(false)}
          >
            <div dangerouslySetInnerHTML={{ __html: termsHtml }} />
          </Modal>
          :
          null
      }
    </SignupPageStyled>
  );
};